import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useDataProvider } from 'react-admin';
import type { RaRecord } from 'react-admin';
import { useFeature } from '../featureFlags/FeatureGate';
import { OPERATIONAL_LABEL } from './fleetFeatures';
import { vehicleTypeLabel } from './vehicleTypes';

/**
 * Grupa pozycji na liście wyboru przy zleceniu — liczy ją API
 * (`apps/api/src/cars/order-picker.ts`), front tylko ją pokazuje.
 */
export type PickerGroup = 'fits' | 'other' | 'unavailable' | 'notForOrders';

export type Vehicle = RaRecord & {
  registrationNumber?: string | null;
  name?: string;
  makeAndModel?: string | null;
  carType?: string | null;
  available?: boolean | null;
  operational?: boolean | null;
  pickerGroup?: PickerGroup;
};

const vehicleLabel = (p: Vehicle): string =>
  p.registrationNumber ?? p.name ?? `#${String(p.id)}`;

/** Nagłówki grup na rozwiniętej liście — w kolejności, w jakiej przychodzą z API. */
export const PICKER_GROUP_HEADINGS: Record<PickerGroup, string> = {
  fits: 'Pasują do zlecenia',
  // „Poza eksploatacją”, nie „niesprawne” (08.10.2026): grupa to flagi
  // `operational` i `available` z TMS, a o usterce żadna z nich nie mówi.
  other: 'Pozostałe dostępne',
  unavailable: 'Niedostępne i poza eksploatacją',
  notForOrders: 'Nie pod zlecenie (osobówki, naczepy, wózki)',
};

/**
 * Nagłówki sprzed 08.10.2026 — przełącznik `operationalLabel` wyłączony
 * (wersja „obecna” = produkcja 0.31.3).
 */
export const PICKER_GROUP_HEADINGS_LEGACY: Record<PickerGroup, string> = {
  ...PICKER_GROUP_HEADINGS,
  other: 'Pozostałe sprawne',
  unavailable: 'Niedostępne i niesprawne',
};

/** Pozycje wyszarzone: auta, których dziś pod zlecenie się nie postawi. */
const isDimmed = (p: Vehicle): boolean =>
  p.pickerGroup === 'unavailable' || p.pickerGroup === 'notForOrders';

/**
 * Wybór pojazdu z kartoteki.
 *
 * Zastępuje wpisywanie identyfikatora z ręki. Przy 1210 pojazdach w kartotece
 * planista nie ma skąd znać numeru wewnętrznego, a pomyłka w cyfrze podstawiała
 * pod zlecenie inne auto — bez żadnego sygnału, bo identyfikator istniał.
 *
 * Domyślnie pytamy o pojazdy **pod zlecenie** (bez naczep i wózków): stanowią
 * one ponad połowę kartoteki, a pod zlecenie się ich nie stawia. Filtr jest po
 * stronie API, więc lista nie przychodzi tu w całości.
 *
 * ## Przy zleceniu (`forOrder`, 06.10.2026)
 *
 * Alfabet po całej flocie stawiał na górze osobówki i auta poza eksploatacją
 * („BMI157 VW · inny typ” — test na dev). Przy zleceniu lista idzie grupami
 * z API: auta, które obsłużą wymagany typ, pozostałe dostępne, potem
 * niedostępne (wyszarzone). Osobówki, naczepy i wózki są ukryte, dopóki
 * planista nie zaznaczy „Pokaż wszystkie pojazdy” — albo nie wpisze numeru:
 * wyszukiwanie znajduje dowolny pojazd, z typem i grupą przy pozycji.
 */
export const VehiclePicker = ({
  value,
  onChange: onPick,
  disabled,
  label = 'Pojazd',
  helperText,
  forOrder,
}: {
  value: number | null;
  onChange: (id: number | null) => void;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  /** Lista wyboru przy zleceniu — typ wymagany przez zlecenie (`assignedVehicleType`). */
  forOrder?: { requiredVehicleType?: string | null };
}) => {
  const dataProvider = useDataProvider();
  const groupHeadings = useFeature(OPERATIONAL_LABEL) ? PICKER_GROUP_HEADINGS : PICKER_GROUP_HEADINGS_LEGACY;
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const pickerMode = forOrder !== undefined;
  const requiredVehicleType = forOrder?.requiredVehicleType ?? null;

  useEffect(() => {
    let active = true;
    // Odczekanie, żeby każda litera nie szła osobnym zapytaniem.
    const timer = setTimeout(() => {
      setLoading(true);
      dataProvider
        .getList('cars', {
          pagination: { page: 1, perPage: 20 },
          sort: { field: 'registrationNumber', order: 'ASC' },
          filter: pickerMode
            ? { q: search, orderPicker: true, requiredVehicleType, showAll }
            : { q: search, ciagniki: true },
        })
        .then(({ data }) => {
          if (active) setOptions(data as Vehicle[]);
        })
        .catch(() => {
          if (active) setOptions([]);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [search, dataProvider, pickerMode, requiredVehicleType, showAll]);

  const selected = useMemo(
    () => options.find((p) => p.id === value) ?? null,
    [options, value],
  );

  const field = (
    <Autocomplete
      size="small"
      sx={{ minWidth: 280 }}
      disabled={disabled}
      options={options}
      loading={loading}
      // Nagłówki grup tylko przy zleceniu — API oddaje pozycje już ułożone
      // grupami, więc MUI nie rozbije jednej grupy na kilka.
      groupBy={
        pickerMode ? (p) => groupHeadings[p.pickerGroup ?? 'other'] : undefined
      }
      value={selected}
      onChange={(_e, p) => onPick(p ? (p.id as number) : null)}
      onInputChange={(_e, v) => setSearch(v)}
      getOptionLabel={vehicleLabel}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      // Filtrowanie robi API, nie przeglądarka — mamy tu tylko 20 podpowiedzi
      // z całej kartoteki, więc domyślne odsiewanie ukrywałoby trafienia.
      filterOptions={(x) => x}
      noOptionsText={search ? 'Brak pojazdu o takim numerze' : 'Wpisz numer rejestracyjny'}
      // Bez lokalizacji MUI te teksty były po angielsku („Loading…”, „Open”,
      // „Clear” w dymkach przycisków) — poprawa brzmienia 01.10.2026.
      loadingText="Ładowanie…"
      openText="Rozwiń"
      closeText="Zwiń"
      clearText="Wyczyść"
      renderOption={(props, p) => (
        <Box
          component="li"
          {...props}
          key={p.id}
          data-picker-group={p.pickerGroup}
          sx={isDimmed(p) ? { opacity: 0.6 } : undefined}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2">{vehicleLabel(p)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[p.makeAndModel, vehicleTypeLabel(p.carType)].filter(Boolean).join(' · ') || '—'}
            </Typography>
          </Box>
          {p.operational === false && (
            <Chip size="small" variant="outlined" label="poza eksploatacją" />
          )}
          {p.operational !== false && p.available === false && (
            <Chip size="small" color="warning" label="niedostępny" />
          )}
          {p.pickerGroup === 'notForOrders' && (
            <Chip size="small" variant="outlined" label="nie pod zlecenie" sx={{ ml: 0.5 }} />
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} helperText={helperText} />
      )}
    />
  );

  if (!pickerMode) return field;
  return (
    <Box>
      {field}
      <FormControlLabel
        sx={{ ml: 0 }}
        control={
          <Checkbox
            size="small"
            checked={showAll}
            disabled={disabled}
            onChange={(e) => setShowAll(e.target.checked)}
          />
        }
        label={
          <Typography variant="body2">Pokaż wszystkie pojazdy</Typography>
        }
      />
    </Box>
  );
};
